import { useCallback, useEffect, useState } from "react";

function getParent(node: Node): Element | ShadowRoot | null {
  const parent = node.parentNode;
  return parent instanceof Element || parent instanceof ShadowRoot
    ? parent
    : null;
}

/** Marks every element outside `dialog` inert, walking up to `<body>` across
 * shadow roots. Skips already-inert and aria-hidden nodes; returns what it marked. */
function markOutsideInert(dialog: HTMLElement): Element[] {
  const marked: Element[] = [];
  const body = dialog.ownerDocument.body;
  let current: Element = dialog;
  for (;;) {
    const parent = getParent(current);
    if (!parent) {
      break;
    }
    for (const sibling of Array.from(parent.children)) {
      if (
        sibling !== current &&
        !sibling.hasAttribute("inert") &&
        sibling.getAttribute("aria-hidden") !== "true"
      ) {
        sibling.setAttribute("inert", "");
        marked.push(sibling);
      }
    }
    if (parent === body) {
      break;
    }
    current = parent instanceof ShadowRoot ? parent.host : parent;
  }
  return marked;
}

/** Like a native `<dialog>`: everything outside the modal is inert while `active`.
 * Returns a ref for the dialog, which receives focus on activation. */
export function useInertPageBehindModal(
  active: boolean,
): (element: HTMLElement | null) => void {
  const [dialog, setDialog] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!active || !dialog) {
      return;
    }
    const marked = markOutsideInert(dialog);
    dialog.focus();

    return () => {
      for (const element of marked) {
        element.removeAttribute("inert");
      }
    };
  }, [active, dialog]);

  return useCallback((element: HTMLElement | null) => setDialog(element), []);
}
